import { useCallback } from 'react';
import useAppContext from '../../../hooks/useAppContext';
import useAnalytics from '../../../hooks/useAnalytics';
import useSendToClock, {
  SendableContentKind,
} from '../../../hooks/useSendToClock';
import useSendCollectionToClockMutation from './useSendCollectionToClockMutation';
import { AlertType } from '../../../design-system/DSAlert';
import { Klydo } from '../../../api/klydo';

type SendableCollection = {
  id: string;
  klydos?: Pick<Klydo, 'subtitle' | 'capabilitiesV2'>[];
};

const useSendCollectionToClock = (
  collection: SendableCollection | undefined,
) => {
  const { showAlert } = useAppContext();
  const { trackEvent } = useAnalytics();

  const { mutate: sendCollection, isPending } =
    useSendCollectionToClockMutation({
      onSuccess: () => {
        // Tracked on success so the metric counts delivered sends, not attempts.
        if (collection) {
          trackEvent({
            type: 'collection_sent_to_clock',
            context: `collectionId:${collection.id}`,
          });
        }
        showAlert({
          message: 'Your collection is on its way to your clock!',
          alertType: AlertType.SUCCESS,
        });
      },
    });

  const send = useCallback(() => {
    if (!collection) return;
    sendCollection({ collectionId: collection.id });
  }, [collection, sendCollection]);

  return useSendToClock({
    kind: SendableContentKind.Collection,
    hasContent: !!collection,
    klydos: collection?.klydos ?? [],
    sentCollectionId: collection?.id,
    send,
    isSending: isPending,
  });
};

export default useSendCollectionToClock;
